import { formatDateTime, type GenerationDto, type RegenerateAffectedDto } from '@pb/shared';
import { generationFileUrl } from '../api/generations';
import { generationKindLabel, pluralize } from '../lib/text';

export function DownloadGeneration({ generation, label = 'Download' }: { generation: GenerationDto; label?: string }) {
  if (!generation.has_file) {
    return <span className="small faint">File not kept</span>;
  }
  return (
    <a className="btn btn-sm" href={generationFileUrl(generation.id)} download>
      {label}
    </a>
  );
}

export function Warnings({ warnings, title = 'Warnings' }: { warnings: string[]; title?: string }) {
  if (warnings.length === 0) return null;
  return (
    <div className="alert alert-warning">
      <strong>{title}</strong>
      <ul>
        {warnings.map((warning, index) => (
          <li key={index}>{warning}</li>
        ))}
      </ul>
    </div>
  );
}

export function GenerationResult({ generation }: { generation: GenerationDto }) {
  return (
    <div className="stack">
      <div className="alert alert-success">
        <div className="row-between">
          <span>
            <strong>{generation.file_name}</strong> — {pluralize(generation.page_count, 'page')}, template version{' '}
            {generation.template_version_no}
          </span>
          <DownloadGeneration generation={generation} />
        </div>
      </div>
      <Warnings warnings={generation.warnings} />
    </div>
  );
}

function GenerationRows({ items }: { items: GenerationDto[] }) {
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Presentation</th>
          <th>File</th>
          <th>Pages</th>
          <th>Warnings</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {items.map((g) => (
          <tr key={g.id}>
            <td>{g.presentation_name}</td>
            <td className="small">{g.file_name}</td>
            <td>{g.page_count}</td>
            <td>
              {g.warnings.length === 0 ? (
                <span className="faint small">None</span>
              ) : (
                <ul className="small" style={{ margin: 0, paddingLeft: 16 }}>
                  {g.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              )}
            </td>
            <td className="table-actions">
              <DownloadGeneration generation={g} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function RegenerateResults({ result }: { result: RegenerateAffectedDto }) {
  if (result.generations.length === 0 && result.snapshots.length === 0) {
    return <div className="alert alert-info">Nothing is affected, so nothing was regenerated.</div>;
  }
  return (
    <div className="stack">
      <div className="alert alert-success">
        Regenerated {pluralize(result.generations.length, 'presentation')} and{' '}
        {pluralize(result.snapshots.length, 'planned-event snapshot')}. Held events were not touched.
      </div>
      {result.generations.length ? (
        <div className="stack">
          <h4>Presentations</h4>
          <GenerationRows items={result.generations} />
        </div>
      ) : null}
      {result.snapshots.length ? (
        <div className="stack">
          <h4>Planned-event snapshots</h4>
          <GenerationRows items={result.snapshots} />
        </div>
      ) : null}
    </div>
  );
}

export function generationMeta(g: GenerationDto): string {
  return `${generationKindLabel(g.kind)} · ${formatDateTime(g.created_at)}${g.author ? ` · ${g.author}` : ''}`;
}
